import { Link } from "react-router-dom";
import { LegalPage, Section, ContactLink } from "@/components/LegalPage";

const S = (props) => <Section prefix="terms" {...props} />;

export default function Terms() {
  return (
    <LegalPage title="Terms of use" testId="terms-page">
      <p className="mt-6 text-[15px] leading-relaxed text-ink-soft">
        These terms apply when you use pitchpaper.au, which is run by Pitchpaper (ABN 87 571 984 745), referred to as "we" or "us".
        By creating an account or buying a download, you agree to them. Please read them together with our{" "}
        <Link to="/privacy" className="text-thread hover:underline" data-testid="terms-privacy-link">privacy policy</Link>.
      </p>

      <S title="Your account" id="account">
        <ul className="list-disc space-y-2 pl-5">
          <li>You must be able to enter a binding agreement, or have a parent or guardian's permission to use Pitchpaper.</li>
          <li>Keep your sign-in details safe. You are responsible for activity on your account.</li>
          <li>One person per account. Please don't share or resell access.</li>
        </ul>
      </S>

      <S title="Free features" id="free">
        <p>Building resumes and cover letters, AI writing help and previews are free. Free previews and free shared links show a "Made with Pitchpaper" watermark.</p>
        <p>To keep the service fair for everyone, AI help has a daily limit for each account. We may change free features or limits at any time.</p>
      </S>

      <S title="Paid downloads" id="paid">
        <p>All prices are in Australian dollars and include GST. You pay once through Stripe. There are no subscriptions and nothing renews automatically.</p>
        <ul className="list-disc space-y-2 pl-5">
          <li><strong className="text-ink">Single Download ($6.99):</strong> unlocks watermark-free PDF downloads of one resume or one cover letter. You can download it again whenever you like, including after you edit it. A duplicated copy counts as a new document and is not unlocked.</li>
          <li><strong className="text-ink">30-Day Pass ($19.99):</strong> unlimited watermark-free downloads of all your documents for 30 days from the moment your payment is confirmed. If you buy another pass while one is active, the new 30 days are added to the end of the current one.</li>
        </ul>
        <p>When a pass ends, your documents stay saved and any PDFs you've already downloaded are yours to keep. Downloads go back to showing the watermark unless a document was unlocked with a Single Download.</p>
        <p>We may change prices in future. Changes never affect a purchase you've already made. You'll receive a tax invoice by email after each purchase, and you can also find it on the Invoices page.</p>
      </S>

      <S title="Refunds" id="refunds">
        <p>Our services come with guarantees that cannot be excluded under the Australian Consumer Law.</p>
        <p>If your download doesn't work, for example the PDF won't download, won't open or is faulty, contact us and we'll fix it.
          If we can't fix it within a reasonable time, or there is a major failure, we'll give you a full refund to your original payment method.</p>
        <p>Apart from this and your other rights under the Australian Consumer Law, we don't offer refunds for change of mind, including when a download worked as described.</p>
        <p>To ask for help or a refund, email <ContactLink testId="terms-refund-email" /> with your invoice number (for example PP-000123).</p>
      </S>

      <S title="AI-written content" id="ai">
        <p>AI suggestions, cover letters, match scores and imports are generated automatically and can contain mistakes. Always check your documents before you send them, and only include information that is true.</p>
        <p>Pitchpaper helps you present yourself well, but we can't promise interviews, job offers or any particular result.</p>
      </S>

      <S title="Your content" id="content">
        <p>You own everything you write or upload. You give us permission to store and process it only so we can run Pitchpaper for you, for example to save your documents, create AI suggestions and make your PDFs.</p>
        <p>If you turn on a public link for a resume, anyone with the link can view it until you turn it off.</p>
      </S>

      <S title="Acceptable use" id="acceptable-use">
        <p>Please don't use Pitchpaper to:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Create false or misleading resumes, such as fake qualifications, employers or references, or pretend to be someone else.</li>
          <li>Upload or share content that is unlawful, offensive, discriminatory or that you don't have the right to use, including other people's personal information without their permission.</li>
          <li>Use bots, scripts or other automated tools, or try to get around AI limits, watermarks or payments.</li>
          <li>Attempt to hack, overload, copy or reverse engineer the site, or upload malware.</li>
          <li>Resell, rent or share paid access or downloads as a service for others.</li>
        </ul>
        <p>If someone breaks these rules, we may suspend or close their account. Where it's reasonable, we'll contact you first.</p>
      </S>

      <S title="Availability" id="availability">
        <p>We work hard to keep Pitchpaper running, but we can't guarantee it will always be available or error-free. We may update, change or pause features, for example for maintenance.</p>
      </S>

      <S title="Liability" id="liability">
        <p>Nothing in these terms excludes, restricts or changes your rights under the Australian Consumer Law or any other law that can't be excluded.</p>
        <p>Subject to that, and to the extent the law allows, we aren't liable for indirect or consequential loss, such as a missed job opportunity. Our total liability to you is limited to supplying the service again or refunding the amount you paid us in the 12 months before the claim.</p>
      </S>

      <S title="Closing your account" id="closing">
        <p>You can stop using Pitchpaper at any time and delete your documents in My documents. To close your account completely, email us. We keep invoices and payment records as Australian tax law requires.</p>
      </S>

      <S title="Changes to these terms" id="changes">
        <p>We may update these terms from time to time. We'll change the date at the top of this page, and if a change is significant we'll let you know on the site or by email. Continuing to use Pitchpaper after a change means you accept the new terms.</p>
      </S>

      <S title="Governing law" id="law">
        <p>These terms are governed by the laws of Victoria, Australia. You and we agree to the jurisdiction of the courts of Victoria.</p>
      </S>

      <S title="Contact" id="contact">
        <p>Questions about these terms? Email <ContactLink testId="terms-contact-email" />.</p>
      </S>
    </LegalPage>
  );
}
